import { motion } from "framer-motion";
import { Zap, Menu, X, User, LogOut, Heart, Settings, Download } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";

const navItems = [
  { label: "Compare Products", href: "/products" },
  { label: "AI Chat", href: "/ai-chat" },
  { label: "Box Builder", href: "/box-calculator" },
  { label: "Wiring", href: "/wiring" },
  { label: "Electrical", href: "/electrical" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const { user, profile, signOut } = useAuth();
  const { canPrompt, isIOS, triggerInstall } = useInstallPrompt();
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <>
      <div className="brand-stripe" />
      <nav className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-md">
      <div className="container flex items-center justify-between h-14">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-primary" />
          <span className="font-display font-bold text-foreground tracking-tight">
            Ohm<span className="text-primary">&</span>Ply
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-4 lg:gap-6 md:ml-8 lg:ml-12">
          {navItems.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className="text-sm font-mono font-semibold text-muted-foreground hover:text-foreground transition-colors uppercase tracking-wider"
            >
              {item.label}
            </Link>
          ))}

          {canPrompt && (
            <button
              onClick={async () => {
                const result = await triggerInstall();
                if (result === "ios") setShowIOSGuide(true);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-primary/40 rounded-sm text-xs font-mono text-primary uppercase tracking-wider hover:bg-primary/10 transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Install App
            </button>
          )}

          {user ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 border border-border rounded-sm bg-surface-raised hover:bg-secondary transition-colors"
              >
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="" className="w-5 h-5 rounded-full" />
                ) : (
                  <User className="w-4 h-4 text-muted-foreground" />
                )}
                <span className="text-xs sm:text-sm font-mono text-foreground max-w-[100px] truncate">
                  {profile?.display_name || user.email?.split("@")[0]}
                </span>
              </button>

              {userMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute right-0 top-full mt-1 w-48 border border-border rounded-sm bg-card shadow-hard z-50"
                >
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 px-3 py-2.5 text-sm font-mono text-muted-foreground hover:text-foreground hover:bg-surface-raised transition-colors"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <Settings className="w-3.5 h-3.5" />
                    Profile
                  </Link>
                  <Link
                    to="/saved"
                    className="flex items-center gap-2 px-3 py-2.5 text-sm font-mono text-muted-foreground hover:text-foreground hover:bg-surface-raised transition-colors"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <Heart className="w-3.5 h-3.5" />
                    Saved Items
                  </Link>
                  <button
                    onClick={() => { signOut(); setUserMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-mono text-muted-foreground hover:text-foreground hover:bg-surface-raised transition-colors border-t border-border"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    Sign Out
                  </button>
                </motion.div>
              )}
            </div>
          ) : (
            <Link to="/auth">
              <motion.button
                whileTap={{ scale: 0.97 }}
                className="px-4 py-2 bg-primary text-primary-foreground font-mono font-bold text-xs uppercase tracking-wider rounded-sm shadow-hard"
              >
                Sign Up Free
              </motion.button>
            </Link>
          )}
        </div>

        {/* Mobile toggle */}
        <button className="md:hidden text-foreground" onClick={() => setOpen(!open)} aria-label={open ? "Close menu" : "Open menu"}>
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden border-t border-border bg-card p-4 space-y-1"
        >
          {navItems.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className="block text-base font-mono text-muted-foreground uppercase tracking-wider py-2.5"
              onClick={() => setOpen(false)}
            >
              {item.label}
            </Link>
          ))}

          {canPrompt && (
            <button
              onClick={async () => {
                const result = await triggerInstall();
                if (result === "ios") setShowIOSGuide(true);
                setOpen(false);
              }}
              className="flex items-center gap-2 text-base font-mono text-primary uppercase tracking-wider py-2.5 font-bold"
            >
              <Download className="w-4 h-4" />
              Install App
            </button>
          )}

          {user ? (
            <>
              <Link
                to="/profile"
                className="block text-base font-mono text-muted-foreground uppercase tracking-wider py-2.5"
                onClick={() => setOpen(false)}
              >
                Profile
              </Link>
              <Link
                to="/saved"
                className="block text-base font-mono text-muted-foreground uppercase tracking-wider py-2.5"
                onClick={() => setOpen(false)}
              >
                Saved Items
              </Link>
              <button
                onClick={() => { signOut(); setOpen(false); }}
                className="block text-base font-mono text-muted-foreground uppercase tracking-wider py-2.5"
              >
                Sign Out
              </button>
            </>
          ) : (
            <Link
              to="/auth"
              className="block text-base font-mono text-primary uppercase tracking-wider py-2.5 font-bold"
              onClick={() => setOpen(false)}
            >
              Sign Up Free
            </Link>
          )}
        </motion.div>
      )}
    </nav>

    {/* iOS Install Guide Modal */}
    {showIOSGuide && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowIOSGuide(false)}>
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-card border border-border rounded-lg p-6 max-w-sm w-full shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="font-display font-bold text-foreground text-lg mb-3">Install on iPhone</h3>
          <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside leading-relaxed">
            <li>Tap the <span className="text-foreground font-medium">Share</span> button ⬆️ in Safari</li>
            <li>Scroll down and tap <span className="text-foreground font-medium">"Add to Home Screen"</span></li>
            <li>Tap <span className="text-foreground font-medium">"Add"</span> — done!</li>
          </ol>
          <button
            onClick={() => setShowIOSGuide(false)}
            className="mt-4 w-full px-4 py-2.5 bg-primary text-primary-foreground font-mono font-bold text-sm rounded-sm"
          >
            Got It
          </button>
        </motion.div>
      </div>
    )}
    </>
  );
};

export default Navbar;
